<script setup>
import { useForm } from '@inertiajs/vue3';
import { computed, getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Card from 'primevue/card';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const confirm = useConfirm();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    exchange: Object,
    globalFixedAdditions: Object,
});

// Convert from integer to decimal with 8 decimal precision
// (e.g., 95000000000 -> 950.00, 660000 -> 0.0066)
const form = useForm({
    currency: props.exchange.currency,
    value: props.exchange.value / 100,
    percentage_addition: props.exchange.percentage_addition || 0,
    apply_percentage_to_total: props.exchange.apply_percentage_to_total || false,
});

const submit = () => {
    form.put(route('exchanges.update', props.exchange.id), {
        onStart: () => showLoading('Updating exchange...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Exchange updated successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update exchange',
                life: 5000
            });
        }
    });
};

const deleteExchange = () => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} "${props.exchange.currency}"? ${$t('This action cannot be undone.')}`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('exchanges.destroy', props.exchange.id), {
                onStart: () => showLoading('Deleting exchange...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Exchange deleted successfully',
                        life: 3000
                    });
                },
                onError: () => {
                    toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete exchange',
                        life: 5000
                    });
                }
            });
        }
    });
};

const getPreviewFormula = computed(() => {
    const globalFixed = props.globalFixedAdditions?.[form.currency] || 0;
    const fixed = globalFixed / 100;
    const percentage = form.percentage_addition
        ? (form.percentage_addition / 10000) + 1
        : 1;
    const percentFormatted = percentage.toFixed(2);

    if (form.apply_percentage_to_total) {
        return `= (COST + ${fixed}) * ${percentFormatted}`;
    } else {
        return `= (COST * ${percentFormatted}) + ${fixed}`;
    }
});

const percentageDisplay = computed({
    get() {
        return form.percentage_addition / 100;
    },
    set(value) {
        form.percentage_addition = value * 100;
    }
});

</script>

<template>
    <AdminMainLayout>
        <div class=" mx-auto p-6">
            <ConfirmDialog />

            <!-- Main Content -->
            <div class="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <div class="lg:col-span-2">
                    <Card class="mb-6">
                    <template #title>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-pencil text-2xl"></i>
                            <span>{{ $t('Edit Exchange Rate') }}</span>
                        </div>
                    </template>

                        <template #content>
                            <form @submit.prevent="submit" class="space-y-6">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="space-y-2">
                                        <label for="currency" class="text-sm font-semibold text-surface-700">
                                            {{ $t('Currency') }} <span class="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            id="currency"
                                            v-model="form.currency"
                                            class="w-full"
                                            :class="{ 'p-invalid': form.errors.currency }"
                                            :placeholder="$t('e.g., USD, BRL, EUR')"
                                            :disabled="form.processing"
                                        />
                                        <small v-if="form.errors.currency" class="text-red-500">{{ form.errors.currency }}</small>
                                    </div>

                                    <div class="space-y-2">
                                        <label for="value" class="text-sm font-semibold text-surface-700">
                                            {{ $t('Value based on the dollar') }} <span class="text-red-500">*</span>
                                        </label>
                                        <InputNumber
                                            id="value"
                                            v-model="form.value"
                                            class="w-full"
                                            :class="{ 'p-invalid': form.errors.value }"
                                            mode="decimal"
                                            :minFractionDigits="2"
                                            :maxFractionDigits="2"
                                            :disabled="form.processing"
                                        />
                                        <small v-if="form.errors.value" class="text-red-500">{{ form.errors.value }}</small>
                                    </div>
                                </div>

                                <!-- Pricing Formula Info Section -->
                                <div class="border-t pt-6">
                                    <h3 class="text-lg text-surface-900 mb-4 flex items-center gap-2">
                                        <i class="pi pi-calculator text-primary-500"></i>
                                        {{ $t('Suggested Price Formula') }}
                                    </h3>

                                    <Message severity="info" :closable="false">
                                        <div class="flex items-start gap-3">
                                            <i class="pi pi-info-circle text-xl mt-0.5"></i>
                                            <div>
                                                <p class="font-semibold mb-2">{{ $t('Pricing formulas are now configured per seller') }}</p>
                                                <p class="text-sm mb-2">
                                                    {{ $t('Each seller can have their own custom pricing formula for each currency. To configure a seller\'s pricing formulas, go to the seller edit page.') }}
                                                </p>
                                                <p class="text-sm">
                                                    {{ $t('The values stored in exchange settings are used as defaults when a seller does not have a custom formula configured.') }}
                                                </p>
                                            </div>
                                        </div>
                                    </Message>

                                    <!-- Default Formula Info (read-only display) -->
                                    <div class="mt-4 p-4 bg-surface-50 rounded-lg">
                                        <label class="text-sm font-semibold text-surface-700 block mb-3">
                                            {{ $t('Default Formula for this Currency') }}
                                        </label>
                                        <div class="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span class="text-surface-500">{{ $t('Formula Type') }}:</span>
                                                <span class="ml-2 font-medium">
                                                    {{ form.apply_percentage_to_total ? $t('Type C: (COST + FIXED) × %') : $t('Type B: (COST × %) + FIXED') }}
                                                </span>
                                            </div>
                                            <div>
                                                <span class="text-surface-500">{{ $t('Percentage') }}:</span>
                                                <span class="ml-2 font-medium">{{ percentageDisplay }}%</span>
                                            </div>
                                        </div>
                                        <div class="mt-3 font-mono text-sm bg-white p-2 rounded border">
                                            {{ getPreviewFormula }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="flex gap-3 pt-6 border-t">
                                    <Button
                                        type="submit"
                                        :label="$t('Save Changes')"
                                        icon="pi pi-check"
                                        :loading="form.processing"
                                        size="large"
                                    />
                                    <Button
                                        :label="$t('Cancel')"
                                        icon="pi pi-times"
                                        severity="secondary"
                                        outlined
                                        size="large"
                                        @click="() => router.visit(route('exchanges.index'))"
                                    />
                                    <Button
                                        :label="$t('Delete')"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        class="p-button-danger ml-auto"
                                        size="large"
                                        @click="deleteExchange"
                                    />
                                </div>
                                <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                    {{ $t('Exchange updated successfully.') }}
                                </Message>
                            </form>
                        </template>
                    </Card>
                </div>

                <div class="space-y-6">
                    <Card>
                        <template #title>
                            <div class="flex items-center gap-2">
                                <i class="pi pi-info-circle"></i>
                                <span>{{ $t('Exchange Information') }}</span>
                            </div>
                        </template>
                        <template #content>
                            <div class="space-y-4">
                                <div>
                                    <label class="text-sm font-semibold text-surface-600">{{ $t('Exchange ID:') }}</label>
                                    <p class="text-surface-900 font-mono">{{ exchange.id }}</p>
                                </div>

                                <div v-if="exchange.created_at">
                                    <label class="text-sm font-semibold text-surface-600">{{ $t('Created') }}</label>
                                    <p class="text-surface-900">{{ new Date(exchange.created_at).toLocaleDateString() }}</p>
                                </div>

                                <div v-if="exchange.updated_at">
                                    <label class="text-sm font-semibold text-surface-600">{{ $t('Last Updated:') }}</label>
                                    <p class="text-surface-900">{{ new Date(exchange.updated_at).toLocaleDateString() }}</p>
                                </div>

                                <Message severity="warn" :closable="false">
                                    <div class="flex items-center gap-3">
                                        <i class="pi pi-exclamation-triangle text-xl"></i>
                                        <div>
                                            <p class="text-sm">{{ $t('Changing exchange rates may affect pricing across the platform.') }}</p>
                                        </div>
                                    </div>
                                </Message>
                            </div>
                        </template>
                    </Card>
                </div>
            </div>
        </div>
    </AdminMainLayout>
</template>

<style scoped>
</style>
